import { useState } from "react";
import { getDataClient } from "@/lib/dataClient";
import { getCurrentUserId, useDataQuery } from "@/hooks/useDataQuery";

// Veli paneli — çocuk bağlantılı veri katmanı.
// Veli, hesabına bağlı (parent_id) öğrencilerin verilerini bu hook'lar üzerinden okur.

export interface VeliChild {
  id: string;
  userId: string;
  fullName: string;
  grade: string | null;
  city: string | null;
}

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0].slice(0, 1).toLocaleUpperCase("tr-TR");
  return (parts[0][0] + parts[parts.length - 1][0]).toLocaleUpperCase("tr-TR");
}

export function childInitials(name: string): string {
  return initialsOf(name);
}

function shortDate(iso: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("tr-TR", { day: "numeric", month: "long" });
}

function fullDate(iso: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" });
}

// Son N günün (bugün dahil) ISO tarih listesini döndürür.
function lastDates(count: number): string[] {
  const dates: string[] = [];
  const now = new Date();
  for (let i = count - 1; i >= 0; i -= 1) {
    const d = new Date(now);
    d.setDate(now.getDate() - i);
    dates.push(
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`,
    );
  }
  return dates;
}

// ---------- Çocuk listesi ----------

export function useVeliChildren() {
  return useDataQuery<VeliChild[]>(async () => {
    const dataClient = getDataClient();
    const userId = await getCurrentUserId();
    if (!userId) return [];

    const { data, error } = await dataClient
      .from("profiles")
      .select("id, user_id, full_name, grade, city")
      .eq("parent_id", userId)
      .eq("role", "ogrenci")
      .order("created_at", { ascending: true });

    if (error) throw error;
    return (data ?? []).map(
      (c: { id: string; user_id: string | null; full_name: string | null; grade: string | null; city: string | null }) => ({
        id: c.id,
        userId: c.user_id ?? c.id,
        fullName: c.full_name ?? "Öğrenci",
        grade: c.grade ?? null,
        city: c.city ?? null,
      }),
    );
  });
}

// Aktif çocuk seçimini de yöneten kolaylaştırıcı hook.
export function useVeliChild() {
  const query = useVeliChildren();
  const [activeUserId, setActiveUserId] = useState<string | null>(null);
  const children = query.data ?? [];
  const activeChild =
    children.find((c) => c.userId === activeUserId) ?? children[0] ?? null;
  return { ...query, children, activeChild, activeUserId, setActiveUserId };
}

// ---------- Çocuğa özel veri fetcher'ları ----------

export interface ChildExamResult {
  id: string;
  name: string;
  date: string;
  correct: number;
  wrong: number;
  blank: number;
  net: number;
  totalQuestions: number;
}

export function useChildExamResults(childUserId: string | null) {
  return useDataQuery<ChildExamResult[]>(
    async () => {
      if (!childUserId) return [];
      const dataClient = getDataClient();
      const { data, error } = await dataClient
        .from("exam_attempts")
        .select("*")
        .eq("user_id", childUserId)
        .order("taken_on", { ascending: false });
      if (error) throw error;
      return (data ?? []).map(
        (e: { id: string; exam_name: string; correct_count: number; wrong_count: number; blank_count: number; net: number; taken_on: string; total_questions: number | null }) => ({
          id: e.id,
          name: e.exam_name,
          date: fullDate(e.taken_on),
          correct: e.correct_count ?? 0,
          wrong: e.wrong_count ?? 0,
          blank: e.blank_count ?? 0,
          net: Number(e.net ?? 0),
          totalQuestions: e.total_questions ?? 0,
        }),
      );
    },
    [childUserId],
  );
}

export interface ChildHomework {
  id: string;
  title: string;
  subject: string;
  teacher: string;
  dueDate: string;
  status: "bekliyor" | "tamamlandi" | "gecikti";
  description: string;
}

export const HOMEWORK_STATUS_LABELS: Record<string, string> = {
  bekliyor: "Bekliyor",
  tamamlandi: "Tamamlandı",
  gecikti: "Gecikti",
};

export const HOMEWORK_STATUS_COLORS: Record<string, string> = {
  bekliyor: "bg-secondary-100 text-secondary-800",
  tamamlandi: "bg-accent-100 text-accent-800",
  gecikti: "bg-red-50 text-red-700",
};

export function useChildHomework(childUserId: string | null) {
  return useDataQuery<ChildHomework[]>(
    async () => {
      if (!childUserId) return [];
      const dataClient = getDataClient();
      const { data, error } = await dataClient
        .from("homework")
        .select("*")
        .eq("user_id", childUserId)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []).map(
        (h: { id: string; title: string; subject: string; teacher_name: string | null; due_date: string | null; status: string; description: string | null }) => ({
          id: h.id,
          title: h.title,
          subject: h.subject,
          teacher: h.teacher_name ?? "",
          dueDate: h.due_date ?? "",
          status: (h.status as ChildHomework["status"]) ?? "bekliyor",
          description: h.description ?? "",
        }),
      );
    },
    [childUserId],
  );
}

export interface ChildCourse {
  id: string;
  name: string;
  icon: string;
  color: string;
  teacher: string;
  progress: number;
  completedTopics: number;
  totalTopics: number;
  weakCount: number;
}

export function useChildCourses(childUserId: string | null) {
  return useDataQuery<ChildCourse[]>(
    async () => {
      if (!childUserId) return [];
      const dataClient = getDataClient();
      const [cRes, pRes] = await Promise.all([
        dataClient.from("courses").select("*").order("sort_order"),
        dataClient.from("course_progress").select("*").eq("user_id", childUserId),
      ]);
      if (cRes.error) throw cRes.error;
      if (pRes.error) throw pRes.error;

      const progMap = new Map(
        (pRes.data ?? []).map((p: { course_id: string }) => [p.course_id, p]),
      );

      return (cRes.data ?? []).map(
        (c: { id: string; name: string; icon: string; color: string; teacher_name: string | null; total_topics: number }) => {
          const p = progMap.get(c.id) as
            | { completed_topics?: number; weak_count?: number }
            | undefined;
          const total = c.total_topics ?? 1;
          const completed = p?.completed_topics ?? 0;
          return {
            id: c.id,
            name: c.name,
            icon: c.icon,
            color: c.color,
            teacher: c.teacher_name ?? "",
            totalTopics: total,
            completedTopics: completed,
            weakCount: p?.weak_count ?? 0,
            progress: Math.round((completed / total) * 100),
          };
        },
      );
    },
    [childUserId],
  );
}

export interface ChildLiveClass {
  id: string;
  title: string;
  subject: string;
  teacher: string;
  date: string;
  time: string;
  status: "planned" | "live" | "ended" | "cancelled";
}

export const LIVE_STATUS_LABELS: Record<string, string> = {
  planned: "Planlandı",
  live: "Canlı",
  ended: "Tamamlandı",
  cancelled: "İptal",
};

export const LIVE_STATUS_COLORS: Record<string, string> = {
  planned: "bg-secondary-100 text-secondary-800",
  live: "bg-red-50 text-red-700",
  ended: "bg-background-100 text-foreground-600",
  cancelled: "bg-background-100 text-foreground-400",
};

export function useChildLiveClasses(childUserId: string | null) {
  return useDataQuery<ChildLiveClass[]>(
    async () => {
      if (!childUserId) return [];
      const dataClient = getDataClient();
      const { data, error } = await dataClient
        .from("live_classes")
        .select("id, title, subject, teacher_name, starts_at, status")
        .contains("student_ids", [childUserId])
        .order("starts_at", { ascending: false });
      if (error) throw error;

      return (data ?? []).map(
        (lc: { id: string; title: string; subject: string; teacher_name: string | null; starts_at: string; status: string }) => {
          const d = new Date(lc.starts_at);
          return {
            id: lc.id,
            title: lc.title,
            subject: lc.subject,
            teacher: lc.teacher_name ?? "",
            date: Number.isNaN(d.getTime())
              ? lc.starts_at
              : d.toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" }),
            time: Number.isNaN(d.getTime())
              ? ""
              : d.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" }),
            status: (lc.status as ChildLiveClass["status"]) ?? "planned",
          };
        },
      );
    },
    [childUserId],
  );
}

export interface WeeklyReport {
  weekMinutes: number;
  studyDays: { date: string; label: string; minutes: number }[];
  totalTasks: number;
  completedTasks: number;
  lastExam: ChildExamResult | null;
  weakTopics: { id: string; subject: string; topic: string; mastery_percent: number }[];
}

const DAY_LABELS = ["Paz", "Pzt", "Sal", "Çar", "Per", "Cum", "Cmt"];

export function useChildWeeklyReport(childUserId: string | null) {
  return useDataQuery<WeeklyReport>(
    async () => {
      if (!childUserId) {
        return {
          weekMinutes: 0,
          studyDays: [],
          totalTasks: 0,
          completedTasks: 0,
          lastExam: null,
          weakTopics: [],
        };
      }
      const dataClient = getDataClient();
      const dates = lastDates(7);
      const startDate = dates[0];
      const today = dates[6];

      const [sessions, tasks, lastExam, weakTopics] = await Promise.all([
        dataClient
          .from("study_sessions")
          .select("studied_on, minutes")
          .eq("user_id", childUserId)
          .gte("studied_on", startDate)
          .lte("studied_on", today),
        dataClient
          .from("daily_tasks")
          .select("id, completed")
          .eq("user_id", childUserId)
          .eq("task_date", today),
        dataClient
          .from("exam_attempts")
          .select("id, exam_name, correct_count, wrong_count, blank_count, net, taken_on, total_questions")
          .eq("user_id", childUserId)
          .order("taken_on", { ascending: false })
          .limit(1),
        dataClient
          .from("weak_topics")
          .select("id, subject, topic, mastery_percent")
          .eq("user_id", childUserId)
          .order("mastery_percent", { ascending: true })
          .limit(6),
      ]);

      if (sessions.error) throw sessions.error;
      if (tasks.error) throw tasks.error;
      if (lastExam.error) throw lastExam.error;
      if (weakTopics.error) throw weakTopics.error;

      const minuteMap = new Map<string, number>();
      (sessions.data ?? []).forEach((s: { studied_on: string; minutes: number }) => {
        minuteMap.set(s.studied_on, (minuteMap.get(s.studied_on) ?? 0) + s.minutes);
      });

      const studyDays = dates.map((iso) => {
        const d = new Date(iso);
        return {
          date: iso,
          label: DAY_LABELS[d.getDay()] ?? "",
          minutes: minuteMap.get(iso) ?? 0,
        };
      });

      const weekMinutes = studyDays.reduce((sum, d) => sum + d.minutes, 0);
      const taskRows = (tasks.data ?? []) as { completed: boolean }[];
      const totalTasks = taskRows.length;
      const completedTasks = taskRows.filter((t) => t.completed).length;

      const exam = (lastExam.data ?? [])[0] as
        | { id: string; exam_name: string; correct_count: number; wrong_count: number; blank_count: number; net: number; taken_on: string; total_questions: number | null }
        | undefined;

      return {
        weekMinutes,
        studyDays,
        totalTasks,
        completedTasks,
        lastExam: exam
          ? {
              id: exam.id,
              name: exam.exam_name,
              date: fullDate(exam.taken_on),
              correct: exam.correct_count ?? 0,
              wrong: exam.wrong_count ?? 0,
              blank: exam.blank_count ?? 0,
              net: Number(exam.net ?? 0),
              totalQuestions: exam.total_questions ?? 0,
            }
          : null,
        weakTopics: (weakTopics.data ?? []).map(
          (w: { id: string; subject: string; topic: string; mastery_percent: number }) => ({
            id: w.id,
            subject: w.subject,
            topic: w.topic,
            mastery_percent: Number(w.mastery_percent ?? 0),
          }),
        ),
      };
    },
    [childUserId],
  );
}

export interface ChildAiAnalysis {
  summary: string;
  learningLevel: string;
  motivationLevel: number;
  strongSubjects: string[];
  weakSubjects: string[];
  weakTopics: { topic: string; mastery: number }[];
  recentNotes: string[];
}

export function useChildAiAnalysis(childUserId: string | null) {
  return useDataQuery<ChildAiAnalysis>(
    async () => {
      if (!childUserId) {
        return {
          summary: "",
          learningLevel: "",
          motivationLevel: 0,
          strongSubjects: [],
          weakSubjects: [],
          weakTopics: [],
          recentNotes: [],
        };
      }
      const dataClient = getDataClient();

      const [profile, memory, weakTopics] = await Promise.all([
        dataClient
          .from("student_ai_profile")
          .select("ai_summary, learning_level, motivation_level, strong_subjects, weak_subjects")
          .eq("user_id", childUserId)
          .maybeSingle(),
        dataClient
          .from("student_ai_memory")
          .select("content")
          .eq("user_id", childUserId)
          .order("created_at", { ascending: false })
          .limit(8),
        dataClient
          .from("weak_topics")
          .select("subject, topic, mastery_percent")
          .eq("user_id", childUserId)
          .order("mastery_percent", { ascending: true })
          .limit(8),
      ]);

      if (profile.error) throw profile.error;
      if (memory.error) throw memory.error;
      if (weakTopics.error) throw weakTopics.error;

      const prof = (profile.data ?? {}) as {
        ai_summary: string | null;
        learning_level: string | null;
        motivation_level: number | null;
        strong_subjects: string[] | null;
        weak_subjects: string[] | null;
      };

      const strong = Array.isArray(prof.strong_subjects)
        ? prof.strong_subjects.filter((s): s is string => typeof s === "string")
        : [];
      const weak = Array.isArray(prof.weak_subjects)
        ? prof.weak_subjects.filter((s): s is string => typeof s === "string")
        : [];

      const topics = (weakTopics.data ?? []).map(
        (w: { subject: string; topic: string; mastery_percent: number }) => ({
          topic: w.subject ? `${w.subject} — ${w.topic}` : w.topic,
          mastery: Number(w.mastery_percent ?? 0),
        }),
      );

      const recentNotes = (memory.data ?? []).map(
        (m: { content: string }) => m.content,
      );

      return {
        summary: prof.ai_summary ?? "",
        learningLevel: prof.learning_level ?? "",
        motivationLevel: Number(prof.motivation_level ?? 0),
        strongSubjects: strong,
        weakSubjects: weak,
        weakTopics: topics,
        recentNotes,
      };
    },
    [childUserId],
  );
}